import { normalInline, parseMarkdown, plainText, type Block, type Inline } from '../page/markdown'
import { LINE_HEIGHT, TOKENS, styledWidth } from './text'

/**
 * A STICKY NOTE'S WORDS ARE MARKDOWN — owner, 2026-11-02: 讓便條貼顯示 Markdown 文本, 並且能夠預覽編輯 Markdown. 就如同我開給你
 * 看的 Notion 頁面一樣. The note keeps its `text`, which is now Markdown (the pages' dialect, page/markdown.ts): plain words
 * are plain Markdown, so a note written before reads as it did. Here it is laid out for the board — still SVG text, never
 * HTML in a foreignObject (WebKit misplaces it under a scaled group; text.ts) — headings larger, lists with their marks,
 * to-dos with their boxes, a quote with its bar, code in monospace on grey, words bold, italic, struck, underlined,
 * coloured. A note of plain paragraphs keeps Miro's look (centred, in the middle, a blank line between paragraphs); any
 * other reads as a document, from the top left.
 *
 * As large as still fits, as the plain note did (it was text.ts fitText's): its size — the chosen one, else the ordinary — is the
 * largest; the words shrink step by step to the paper, and past the smallest step the rest is cut with "…".
 *
 * Pure: ElementView draws what this returns.
 */

/** A run of words in one style, as one <tspan>. */
export type NoteSeg = {
  text: string
  bold?: boolean
  italic?: boolean
  mono?: boolean
  strike?: boolean
  underline?: boolean
  /** A colour of its own (a link's, a coloured word's, a done to-do's); else the note's ink. */
  color?: string
}

/** What the board draws, in the note's text box (x right, y down from its top-left). */
export type NoteDraw =
  | { k: 'text'; x: number; y: number; fs: number; segs: NoteSeg[]; anchor: 'start' | 'middle' | 'end' }
  | { k: 'rect'; x: number; y: number; w: number; h: number; fill: string }
  | { k: 'box'; x: number; y: number; size: number; checked: boolean }

export type NoteLayout = {
  fs: number
  draws: NoteDraw[]
  /** Each row's words, plain — what a reader of the note's lines (and the tests) counts. */
  lines: string[]
  /** How tall it all stands. */
  height: number
}

/** Notion's colours for words on light paper (BlockNote names them the same). */
const COLORS: Record<string, string> = {
  gray: '#787774',
  brown: '#9f6b53',
  orange: '#d9730d',
  yellow: '#cb912f',
  green: '#448361',
  blue: '#337ea9',
  purple: '#9065b0',
  pink: '#c14c8a',
  red: '#d44c47',
}
const LINK = '#337ea9'
const MUTED = '#9b9a97'
const CODE_BG = 'rgba(0, 0, 0, 0.06)'
const BAR = 'rgba(0, 0, 0, 0.35)'
const RULE = 'rgba(0, 0, 0, 0.18)'

/** A heading's size, against the note's own (h1 … h6). */
const HEADING = [1.45, 1.25, 1.12, 1.05, 1, 1]
const LIST = new Set(['bulletListItem', 'numberedListItem', 'checkListItem'])
const BULLETS = ['•', '◦', '▪']

type Style = Omit<NoteSeg, 'text'>
type Row = {
  segs: NoteSeg[]
  fs: number
  /** Where its words start, from the box's left. */
  x: number
  /** Space above it. */
  gap: number
  /** Drawn in the indent before it (a list's mark, a to-do's box). */
  mark?: { kind: 'text'; text: string; x: number } | { kind: 'box'; x: number; checked: boolean }
  /** Grey behind it (code), a bar at its left (a quote), or a rule across (a divider). */
  under?: 'code' | 'quote' | 'rule'
  underX?: number
}

/** A block's words as runs in their styles. */
function segsOf(content: unknown, base: Style): NoteSeg[] {
  const out: NoteSeg[] = []
  const put = (text: string, s: Record<string, unknown>, extra: Style = {}) => {
    const color = typeof s['textColor'] === 'string' ? COLORS[s['textColor']] : undefined
    out.push({
      text,
      ...base,
      ...(s['bold'] ? { bold: true } : {}),
      ...(s['italic'] ? { italic: true } : {}),
      ...(s['code'] ? { mono: true } : {}),
      ...(s['strike'] ? { strike: true } : {}),
      ...(s['underline'] ? { underline: true } : {}),
      ...(color && !base.color ? { color } : {}),
      ...extra,
    })
  }
  for (const r of normalInline(content) as Inline[]) {
    if (r.type === 'link') for (const c of r.content) put(c.text, c.styles, { color: base.color ?? LINK, underline: true })
    else put(r.text, r.styles)
  }
  return out
}

/** Runs wrapped into lines no wider than `max` — at spaces, between CJK characters, a word too long cut where it must. */
function wrapSegs(segs: NoteSeg[], fs: number, max: number): NoteSeg[][] {
  const lines: NoteSeg[][] = [[]]
  let width = 0
  const line = () => lines[lines.length - 1]!
  const add = (s: NoteSeg, w: number) => {
    const l = line()
    const last = l[l.length - 1]
    if (last && sameStyle(last, s)) last.text += s.text
    else l.push({ ...s })
    width += w
  }
  const newLine = () => {
    const l = line()
    const last = l[l.length - 1]
    if (last) last.text = last.text.replace(/\s+$/, '')
    lines.push([])
    width = 0
  }
  for (const seg of segs) {
    const parts = seg.text.split('\n')
    parts.forEach((part, pi) => {
      if (pi > 0) newLine()
      for (const tok of part.match(TOKENS) ?? []) {
        const t = { ...seg, text: tok }
        const blank = /^\s+$/.test(tok)
        if (blank && !line().length) continue
        const tw = styledWidth(tok, fs, seg)
        if (width + tw <= max + 0.5) {
          add(t, tw)
          continue
        }
        if (blank) {
          newLine()
          continue
        }
        if (line().length) newLine()
        if (tw <= max + 0.5) {
          add(t, tw)
          continue
        }
        for (const ch of tok) {
          const cw = styledWidth(ch, fs, seg)
          if (line().length && width + cw > max + 0.5) newLine()
          add({ ...seg, text: ch }, cw)
        }
      }
    })
  }
  const l = line()
  const last = l[l.length - 1]
  if (last) last.text = last.text.replace(/\s+$/, '')
  return lines
}

function sameStyle(a: Style, b: Style): boolean {
  return !!a.bold === !!b.bold && !!a.italic === !!b.italic && !!a.mono === !!b.mono && !!a.strike === !!b.strike && !!a.underline === !!b.underline && a.color === b.color
}

/** The rows of `blocks` at size `fs` in a box `w` wide. */
function rowsOf(blocks: Block[], fs: number, w: number, plain: boolean, base: Style): Row[] {
  const rows: Row[] = []
  const lh = fs * LINE_HEIGHT
  const walk = (bs: Block[], indent: number) => {
    let n = 0
    bs.forEach((b, i) => {
      const prev = bs[i - 1]
      const first = rows.length === 0
      // A blank line between plain paragraphs (as the note's words always had); a little air between a document's blocks.
      let gap = first ? 0 : plain ? lh : prev && LIST.has(prev.type) && LIST.has(b.type) ? 0 : fs * 0.35
      if (b.type === 'heading' && !first) gap = Math.max(gap, fs * 0.6)
      const p = b.props ?? {}
      const avail = Math.max(fs, w - indent)
      const push = (lines: NoteSeg[][], size: number, x: number, extra: Partial<Row> = {}) => {
        lines.forEach((segs, k) => rows.push({ segs, fs: size, x, gap: k === 0 ? gap : 0, ...(k === 0 ? extra : { ...(extra.under ? { under: extra.under, underX: extra.underX ?? x } : {}) }) }))
      }
      n = b.type === 'numberedListItem' ? (prev?.type === 'numberedListItem' ? n + 1 : Number(p['start'] ?? 1) || 1) : 0
      switch (b.type) {
        case 'heading': {
          const size = fs * (HEADING[Math.min(5, Math.max(0, Number(p['level'] ?? 1) - 1))] ?? 1)
          push(wrapSegs(segsOf(b.content, { ...base, bold: true }), size, avail), size, indent)
          break
        }
        case 'bulletListItem':
        case 'numberedListItem':
        case 'checkListItem':
        case 'toggleListItem': {
          const markW = fs * 1.25
          const checked = b.type === 'checkListItem' && p['checked'] === true
          const segs = segsOf(b.content, checked ? { ...base, strike: true, color: MUTED } : base)
          const mark: Row['mark'] =
            b.type === 'checkListItem'
              ? { kind: 'box', x: indent, checked }
              : { kind: 'text', x: indent, text: b.type === 'numberedListItem' ? `${n}.` : b.type === 'toggleListItem' ? '▸' : (BULLETS[(indent / markW) % 3 | 0] ?? '•') }
          const lines = wrapSegs(segs, fs, Math.max(fs, avail - markW))
          push(lines.length ? lines : [[]], fs, indent + markW, { mark })
          // A toggle shows its title; what it holds stays folded, as Notion's does.
          if (b.type !== 'toggleListItem' && b.children.length) walk(b.children, indent + markW)
          return
        }
        case 'quote': {
          const bar = fs * 0.75
          push(wrapSegs(segsOf(b.content, base), fs, Math.max(fs, avail - bar)), fs, indent + bar, { under: 'quote', underX: indent })
          break
        }
        case 'codeBlock': {
          const size = fs * 0.88
          const pad = fs * 0.4
          const text = plainText(b.content)
          const lines = wrapSegs([{ ...base, text: text || ' ', mono: true }], size, Math.max(size, avail - pad * 2))
          push(lines, size, indent + pad, { under: 'code', underX: indent })
          break
        }
        case 'divider':
          rows.push({ segs: [], fs: fs * 0.6, x: indent, gap, under: 'rule', underX: indent })
          break
        case 'table': {
          const t = (b.content ?? {}) as { rows?: Array<{ cells?: unknown[] }> }
          const lines: NoteSeg[][] = []
          for (const r of t.rows ?? []) {
            const cells = (r.cells ?? []).map((c) => plainText(c && typeof c === 'object' && !Array.isArray(c) ? (c as { content?: unknown }).content : c))
            lines.push(...wrapSegs([{ ...base, text: cells.join('  │  ') }], fs, avail))
          }
          push(lines, fs, indent)
          break
        }
        case 'image':
          push([[{ ...base, text: `[${String(p['caption'] || 'picture')}]`, color: MUTED }]], fs, indent)
          break
        default:
          push(wrapSegs(segsOf(b.content, base), fs, avail), fs, indent)
      }
      if (b.children.length) walk(b.children, indent + fs * 1.25)
    })
  }
  walk(blocks, 0)
  return rows
}

const rowHeight = (r: Row) => r.fs * LINE_HEIGHT
const heightOf = (rows: Row[]) => rows.reduce((h, r) => h + r.gap + rowHeight(r), 0)

/** Rows into what is drawn: centred in the middle for plain words (Miro's note), from the top left for a document. */
function draw(rows: Row[], box: { w: number; h: number }, plain: boolean, align: 'left' | 'center' | 'right'): NoteDraw[] {
  const out: NoteDraw[] = []
  const total = heightOf(rows)
  let y = plain ? Math.max(0, (box.h - total) / 2) : 0
  const anchor = !plain || align === 'left' ? 'start' : align === 'right' ? 'end' : 'middle'
  for (const r of rows) {
    y += r.gap
    const lh = rowHeight(r)
    const base = y + (lh - r.fs) / 2 + r.fs * 0.82
    if (r.under === 'code') out.push({ k: 'rect', x: r.underX ?? 0, y, w: box.w - (r.underX ?? 0), h: lh, fill: CODE_BG })
    if (r.under === 'quote') out.push({ k: 'rect', x: r.underX ?? 0, y: y + lh * 0.1, w: Math.max(1.5, r.fs * 0.12), h: lh * 0.8, fill: BAR })
    if (r.under === 'rule') out.push({ k: 'rect', x: r.underX ?? 0, y: y + lh / 2, w: box.w - (r.underX ?? 0), h: 1, fill: RULE })
    if (r.mark?.kind === 'box') {
      const size = r.fs * 0.8
      out.push({ k: 'box', x: r.mark.x + r.fs * 0.1, y: y + (lh - size) / 2, size, checked: r.mark.checked })
    } else if (r.mark?.kind === 'text') {
      out.push({ k: 'text', x: r.mark.x + r.fs * 0.15, y: base, fs: r.fs, segs: [{ text: r.mark.text }], anchor: 'start' })
    }
    if (r.segs.length) {
      const x = anchor === 'start' ? r.x : anchor === 'end' ? box.w : box.w / 2
      out.push({ k: 'text', x, y: base, fs: r.fs, segs: r.segs, anchor })
    }
    y += lh
  }
  return out
}

const cache = new Map<string, NoteLayout>()

/**
 * A note's Markdown laid out in its text box `w`×`h`, as large as still fits: `steps` from largest to smallest; past the
 * smallest, cut — the last row kept ends in "…". `bold`: the whole note is bold (its B); `align`: a plain note's.
 */
export function noteLayout(text: string, w: number, h: number, steps: number[], opts: { bold?: boolean | undefined; align?: 'left' | 'center' | 'right' | undefined } = {}): NoteLayout {
  const align = opts.align ?? 'center'
  const key = `${w}x${h}|${steps.join(',')}|${opts.bold ? 1 : 0}|${align}|${text}`
  const hit = cache.get(key)
  if (hit) return hit
  const blocks = parseMarkdown(text)
  const plain = blocks.every((b) => b.type === 'paragraph' && !b.children.length)
  const base: Style = opts.bold ? { bold: true } : {}
  let fs = steps[steps.length - 1] ?? 10
  let rows: Row[] | null = null
  for (const s of steps) {
    const r = rowsOf(blocks, s, w, plain, base)
    if (heightOf(r) <= h + 0.5) {
      fs = s
      rows = r
      break
    }
  }
  if (!rows) {
    // Too much for the paper even at the smallest size: what fits, the last row kept ending in "…".
    const all = rowsOf(blocks, fs, w, plain, base)
    rows = []
    let used = 0
    for (const r of all) {
      if (used + r.gap + rowHeight(r) > h + 0.5) break
      used += r.gap + rowHeight(r)
      rows.push(r)
    }
    if (!rows.length && all.length) rows.push(all[0]!)
    const last = rows[rows.length - 1]
    if (last && rows.length < all.length) {
      const tail = last.segs[last.segs.length - 1]
      if (tail) last.segs = [...last.segs.slice(0, -1), { ...tail, text: `${tail.text}…` }]
      else last.segs = [{ ...base, text: '…' }]
    }
  }
  const out: NoteLayout = {
    fs,
    draws: draw(rows, { w, h }, plain, align),
    lines: rows.map((r) => r.segs.map((s) => s.text).join('')),
    height: heightOf(rows),
  }
  if (cache.size > 2000) cache.clear()
  cache.set(key, out)
  return out
}
